import { getElement, PageStatus, normalizeForSearch, renderSortMenu } from "./generics";
import { sortSubjects, renderSubject, Subject, SubjectSortField, SubjectCategory, SubjectSemester, SEMESTER_LABELS } from "./subjects";
import { api } from "./api";
import { renderAddSubjectButton } from "./addSubject";

// Odpowiednik browser.ts (lista wykładowców wydziału) dla katalogu modułu "Przedmioty" - ten
// sam wzorzec (jedno zapytanie na starcie, wyszukiwanie/sortowanie/scroll lokalnie w JS,
// duży przycisk "Dodaj przedmiot" znikający przy długiej liście), plus dodatkowy filtr po
// kategorii USOS (bez odpowiednika w browser.ts).
const searchInput      = getElement<HTMLInputElement>("search-input");
const subjectList       = getElement<HTMLDivElement>("subject-list");
const statusBar         = getElement<HTMLParagraphElement>("status-bar");
const sortMenuContainer = getElement<HTMLDivElement>("sort-menu-container");
const addSubjectContainer = getElement<HTMLDivElement>("add-subject-container");
const categoryFilterContainer = getElement<HTMLDivElement>("category-filter");
const browseInfo        = getElement<HTMLDivElement>("browse-subjects-info");

const universitySlug: string = browseInfo.dataset.universitySlug!;
const facultySlug: string = browseInfo.dataset.facultySlug!;
const facultyId: number = Number(browseInfo.dataset.facultyId!);

// Patrz MANY_POSTS_THRESHOLD w browser.ts - identyczny wzorzec/wartość, dla spójności z resztą
// serwisu (przy 393 zaimportowanych przedmiotach MIMUW ten duży przycisk i tak w praktyce
// zawsze zostaje schowany - zostaje tylko mały pasek pod wynikami wyszukiwania).
const MANY_SUBJECTS_THRESHOLD = 35;

const SORT_STORAGE_KEY = "mumin_browse_subjects_sort";
const VALID_SORTS: SubjectSortField[] = ["most_comments", "name", "rating"];
const SORT_OPTIONS: { value: SubjectSortField, label: string }[] = [
    { value: "most_comments", label: "Najwięcej opinii" },
    { value: "name", label: "Nazwa" },
    { value: "rating", label: "Ocena" },
];

function loadSavedSort(): SubjectSortField {
    try {
        const saved = localStorage.getItem(SORT_STORAGE_KEY);
        if (saved && (VALID_SORTS as string[]).includes(saved)) return saved as SubjectSortField;
    } catch { /* ignore */ }
    return "most_comments";
}

function saveSort(sortBy: SubjectSortField): void {
    try { localStorage.setItem(SORT_STORAGE_KEY, sortBy); } catch { /* ignore */ }
}

interface AppState {
    subjects: Subject[];
    categories: SubjectCategory[];
    status: PageStatus;
    error: string;
    sortBy: SubjectSortField;
    activeCategoryId: number | null;
    activeSemester: SubjectSemester;
    visibleCount: number;
}

let state: AppState = {
    subjects: [],
    categories: [],
    status: "idle",
    error: "",
    sortBy: loadSavedSort(),
    activeCategoryId: null,
    activeSemester: "",
    visibleCount: 20,
};

const observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
        state.visibleCount += 20;
        render();
    }
}, { rootMargin: "100px" });

// Ten sam widżet "przycisk-ikona + rozwijana lista" co "Sortuj" (renderSortMenu w generics.ts) -
// zamiast osobnego, zawsze widocznego <select>, żeby siedział w tym samym wierszu co wyszukiwarka
// i przycisk sortowania, rozwijając się dopiero po kliknięciu. Budowany raz (kategorie się nie
// zmieniają po wczytaniu) - kolejne wywołania (np. po dociągnięciu danych po "pageshow") są no-op.
let categoryMenuBuilt = false;
function renderCategoryFilter(): void {
    if (categoryMenuBuilt || state.categories.length === 0) return;
    categoryMenuBuilt = true;

    const options: { value: string, label: string }[] = [
        { value: "", label: "Wszystkie kategorie" },
        ...state.categories.map(cat => ({ value: String(cat.id), label: cat.name })),
    ];
    const initialValue = state.activeCategoryId !== null ? String(state.activeCategoryId) : "";

    const menu = renderSortMenu(options, initialValue, (value) => {
        state.activeCategoryId = value ? Number(value) : null;
        state.visibleCount = 20;
        render();
    }, "Grupy przedmiotów");
    // Nazwy grup bywają długie (np. "Grupa przedmiotów obieralnych dla informatyki
    // magisterskiej - specjalność Kryptografia") - szerszy panel niż domyślne .sort-menu-dropdown
    // (dzielone z krótkimi opcjami sortowania), żeby tekst zajmował mniej linii w pionie.
    menu.element.classList.add("category-filter-menu");
    categoryFilterContainer.appendChild(menu.element);
}

// Dokłada sekcję "Semestr" (na wyraźną prośbę - "jako drugie pole") do TEGO SAMEGO panelu co
// "Sortuj", zamiast osobnego widżetu obok - renderSortMenu (generics.ts) nie zna pojęcia
// semestru (współdzielona z browser.ts/post_detail.ts, gdzie to nie ma sensu), więc dokładamy
// to ręcznie do już zbudowanego DOM-u, zamiast dodawać ten koncept do generycznego komponentu.
function appendSemesterSection(sortMenuElement: HTMLElement): void {
    const dropdown = sortMenuElement.querySelector(".sort-menu-dropdown");
    if (!dropdown) return;

    const divider = document.createElement("hr");
    divider.className = "sort-menu-divider";
    dropdown.appendChild(divider);

    const heading = document.createElement("div");
    heading.className = "sort-menu-section-label";
    heading.textContent = "Semestr";
    dropdown.appendChild(heading);

    const row = document.createElement("div");
    row.className = "sort-menu-semester-row";

    const options: { value: SubjectSemester, label: string }[] = [
        { value: "", label: "Wszystkie" },
        { value: "Z", label: SEMESTER_LABELS.Z },
        { value: "L", label: SEMESTER_LABELS.L },
    ];
    const buttons = options.map(opt => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "sort-menu-semester-btn";
        btn.textContent = opt.label;
        btn.classList.toggle("active", state.activeSemester === opt.value);
        btn.addEventListener("click", () => {
            state.activeSemester = opt.value;
            state.visibleCount = 20;
            buttons.forEach((b, i) => b.classList.toggle("active", options[i].value === opt.value));
            render();
        });
        row.appendChild(btn);
        return btn;
    });
    dropdown.appendChild(row);
}

function render(): void {
    observer.disconnect();
    subjectList.innerHTML = "";

    if (state.status === "loading") {
        statusBar.textContent = "Ładowanie...";
        return;
    }
    if (state.status === "error") {
        statusBar.textContent = `Error: ${state.error}`;
        return;
    }

    const query = normalizeForSearch(searchInput.value);
    let filtered = state.subjects;

    if (state.activeCategoryId !== null) {
        filtered = filtered.filter(s => s.categories.some(c => c.id === state.activeCategoryId));
    }

    if (state.activeSemester) {
        filtered = filtered.filter(s => s.semester === state.activeSemester);
    }

    if (query) {
        filtered = filtered.filter(s => normalizeForSearch(s.name).includes(query));
    }

    const sorted = sortSubjects(filtered, query ? "name" : state.sortBy);
    statusBar.textContent = query && sorted.length > 0 ? `${sorted.length} znalezionych wyników` : "";

    const visible = sorted.slice(0, state.visibleCount);
    visible.forEach(subject => {
        subjectList.appendChild(renderSubject(subject, universitySlug, facultySlug));
    });

    const resultsFitOnScreen = subjectList.getBoundingClientRect().bottom <= window.innerHeight;
    const reachedEnd = state.visibleCount >= sorted.length;

    if (!reachedEnd) {
        const sentinel = document.createElement("div");
        sentinel.id = "scroll-sentinel";
        sentinel.style.height = "20px";
        subjectList.appendChild(sentinel);
        observer.observe(sentinel);
    }

    if (query) {
        const prompt = document.createElement("div");
        prompt.className = "add-lecturer-prompt";
        const question = document.createElement("p");
        question.className = "add-lecturer-question";
        question.textContent = "Nie znalazłeś przedmiotu?";
        prompt.appendChild(question);
        subjectList.appendChild(prompt);
        renderAddSubjectButton(
            prompt, facultyId, universitySlug, facultySlug, () => searchInput.value,
            true, resultsFitOnScreen || state.subjects.length > MANY_SUBJECTS_THRESHOLD,
            searchInput.value.trim(), resultsFitOnScreen,
        );
    } else if (state.status === "success" && sorted.length === 0 && state.subjects.length === 0) {
        const empty = document.createElement("div");
        empty.className = "empty-faculty-state";
        const heading = document.createElement("p");
        heading.className = "empty-faculty-heading";
        heading.textContent = "Nikt jeszcze nie dodał tu żadnego przedmiotu.";
        const sub = document.createElement("p");
        sub.className = "empty-faculty-subtext";
        sub.textContent = "Dodaj go przyciskiem „Dodaj przedmiot” powyżej.";
        empty.append(heading, sub);
        subjectList.appendChild(empty);
    }
}

function updateAddSubjectVisibility(): void {
    addSubjectContainer.hidden = state.subjects.length > MANY_SUBJECTS_THRESHOLD;
}

async function loadSubjects(): Promise<void> {
    state = { ...state, status: "loading" };
    render();

    const res = await api.getSubjectsCatalog(facultyId);
    if (!res.ok) {
        state = { ...state, status: "error", error: res.error };
        render();
        return;
    }

    const categoryMap = new Map<number, SubjectCategory>();
    res.data.subjects.forEach(s => s.categories.forEach(c => categoryMap.set(c.id, c)));
    const categories = [...categoryMap.values()].sort((a, b) => a.name.localeCompare(b.name));

    state = { ...state, subjects: res.data.subjects, categories, status: "success", visibleCount: 20 };
    updateAddSubjectVisibility();
    renderCategoryFilter();
    render();
}

const sortMenu = renderSortMenu(SORT_OPTIONS, state.sortBy, (sortBy) => {
    saveSort(sortBy);
    state = { ...state, sortBy, visibleCount: 20 };
    render();
}, "Sortuj");
appendSemesterSection(sortMenu.element);
sortMenuContainer.appendChild(sortMenu.element);

renderAddSubjectButton(addSubjectContainer, facultyId, universitySlug, facultySlug, () => searchInput.value);

loadSubjects();

window.addEventListener("pageshow", (event) => {
    if (event.persisted) loadSubjects();
});

searchInput.addEventListener("input", () => {
    state.visibleCount = 20;
    render();
});
